import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, X, Pencil, Loader2, Copy, ShieldCheck, ArrowRight, CalendarClock, FileWarning, Eye, FileText } from "lucide-react";

const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const BAND_DOT = { high: "bg-emerald-500", medium: "bg-amber-500", low: "bg-red-500" };
const STATUS_BADGE = {
  pending: { label: "Offen", cls: "bg-slate-100 text-slate-600" },
  needs_review: { label: "Prüfen", cls: "bg-red-50 text-red-700" },
  confirmed: { label: "Bestätigt", cls: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Abgelehnt", cls: "bg-slate-100 text-slate-400 line-through" },
  committed: { label: "Übernommen", cls: "bg-emerald-100 text-emerald-800" },
};

const STEPS = [
  { key: "external_invoice", label: "Einnahmen", hint: "Deine Ausgangsrechnungen aus dem alten Tool — prüfe Beträge und Kunden." },
  { key: "expense", label: "Ausgaben", hint: "Belege und Eingangsrechnungen — prüfe Beträge und Kategorien." },
  { key: "customer", label: "Kunden", hint: "Deine Kontakte und Kundenstammdaten." },
  { key: "article", label: "Standardartikel", hint: "Wiederverwendbare Rechnungspositionen — prüfe Preise und USt-Sätze." },
];

export default function ReviewCockpit({ migration, onReload }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [confirmCommit, setConfirmCommit] = useState(false);
  const [showBulkDate, setShowBulkDate] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const stats = migration.stats?.by_category || {};
  const current = STEPS[step];
  const tab = current.key;
  const isLast = step === STEPS.length - 1;

  const load = useCallback(async (category) => {
    setLoading(true);
    try {
      const res = await api.get(`/migration/items?category=${category}&page_size=200`);
      setItems(res.data.items);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { setSelected(new Set()); load(tab); }, [tab, load]);

  // Optimistisches Status-Update — kein Full-Reload, kein Sprung nach oben.
  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await api.patch(`/migration/items/${id}`, { status });
    } catch (e) {
      toast.error("Statusänderung fehlgeschlagen");
      load(tab);
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleSelectAll = () =>
    setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))));

  // Bulk-Aktion über die aktuelle Auswahl — optimistisch, ohne Full-Reload.
  const bulkAction = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    const newStatus = action === "confirm" ? "confirmed" : "rejected";
    setItems((prev) => prev.map((it) => (selected.has(it.id) ? { ...it, status: newStatus } : it)));
    setSelected(new Set());
    try {
      const res = await api.post("/migration/items/bulk", { ids, action });
      toast.success(`${res.data.updated} ${action === "confirm" ? "bestätigt" : "abgelehnt"}`);
    } catch (e) {
      toast.error("Massenaktion fehlgeschlagen");
      load(tab);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      await api.post("/migration/commit");
      toast.success("Daten erfolgreich übernommen");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally { setCommitting(false); setConfirmCommit(false); }
  };

  return (
    <div className="space-y-5" data-testid="review-cockpit">
      {/* Geführter Schritt-Indikator */}
      <div className="flex items-center gap-2" data-testid="review-step-indicator">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => setStep(i)}
              data-testid={`review-step-${s.key}`}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                i === step ? "bg-emerald-600 text-white" : i < step ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-50"}`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                i === step ? "bg-white/20" : i < step ? "bg-emerald-100" : "bg-slate-100"}`}>
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span className="hidden sm:inline">{s.label}</span>
              <span className="text-xs opacity-70">{stats[s.key] || 0}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`h-0.5 w-4 flex-shrink-0 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      {tab === "external_invoice" && <ReconcileBar />}

      <UnprocessedBelegeBanner migration={migration} onReprocess={onReload} country={user?.country} />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900" data-testid="review-step-title">
            Schritt {step + 1}/{STEPS.length}: {current.label}
          </h2>
          <p className="text-sm text-slate-500">{current.hint}</p>
        </div>
        <div className="flex gap-2">
          {tab === "external_invoice" && (
            <Button variant="outline" size="sm" onClick={() => setShowBulkDate(true)} data-testid="bulk-date-btn">
              <CalendarClock className="h-4 w-4 mr-1" /> Zahldatum setzen
            </Button>
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-emerald-800">{selected.size} ausgewählt</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => bulkAction("confirm")} data-testid="bulk-confirm-selected">
              <Check className="h-4 w-4 mr-1" /> Bestätigen
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction("reject")} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-reject-selected">
              <X className="h-4 w-4 mr-1" /> Ablehnen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear-selection">
              Aufheben
            </Button>
          </div>
        </div>
      )}

      <div className="min-h-[120px]">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-400 py-12 text-sm">Keine Einträge in dieser Kategorie.</p>
        ) : (
          <ItemsTable
            category={tab} items={items} onEdit={setEditItem} onStatus={setStatus}
            selected={selected} onToggleSelect={toggleSelect} onToggleSelectAll={toggleSelectAll}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="review-prev-btn">
          Zurück
        </Button>
        {isLast ? (
          <Button onClick={() => setConfirmCommit(true)} data-testid="commit-btn">
            Daten übernehmen <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="review-next-btn">
            Weiter: {STEPS[step + 1].label} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>

      {editItem && (
        <EditDialog item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(tab); }} />
      )}

      <BulkDateDialog open={showBulkDate} onClose={() => setShowBulkDate(false)} onApplied={() => { setShowBulkDate(false); load("external_invoice"); }} />

      <AlertDialog open={confirmCommit} onOpenChange={setConfirmCommit}>
        <AlertDialogContent data-testid="commit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Daten jetzt übernehmen?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle nicht abgelehnten, fehlerfreien Einträge werden in deine Einnahmen, Ausgaben, Kunden und
              Standardartikel übernommen. Duplikate werden automatisch übersprungen. Dieser Schritt schließt die Migration ab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={commit} disabled={committing} data-testid="commit-confirm-action">
              {committing ? "Übernimmt…" : "Jetzt übernehmen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Abstimm-Check (Reconcile) ────────────────────────────────────────────────
function ReconcileBar() {
  const year = new Date().getFullYear() - 1;
  const [y, setY] = useState(year);
  const [expected, setExpected] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    try {
      const res = await api.post("/migration/reconcile", { year: Number(y), expected_revenue: Number(expected) || 0 });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abstimmung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="reconcile-bar">
      <CardContent className="py-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        <div className="flex items-end gap-2 flex-wrap">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mb-2" />
          <div>
            <Label className="text-xs">Jahr</Label>
            <Input type="number" min="2000" max="2100" value={y} onChange={(e) => setY(e.target.value)} className="w-24 h-9" data-testid="reconcile-year" />
          </div>
          <div>
            <Label className="text-xs">Jahresumsatz lt. altem Tool (€)</Label>
            <Input type="number" value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="z.B. 48500" className="w-44 h-9" data-testid="reconcile-expected" />
          </div>
          <Button variant="outline" size="sm" onClick={check} disabled={busy} data-testid="reconcile-check">
            {busy ? "Prüft…" : "Abgleichen"}
          </Button>
          {result && (
            <Badge className={result.complete ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="reconcile-result">
              {result.complete
                ? `✓ Vollständig (${eur(result.migrated_sum)})`
                : `Δ ${eur(result.delta)} — migriert ${eur(result.migrated_sum)}`}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Banner: nicht verarbeitete Beleg-PDFs nachträglich per KI auslesen ───────
function UnprocessedBelegeBanner({ migration, onReprocess, country }) {
  const [beleg, setBeleg] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  useEffect(() => {
    api.get("/migration/files")
      .then((res) => setBeleg((res.data || []).filter(
        (f) => ["receipt_pdf", "invoice_pdf", "image"].includes(f.detected_type) &&
          f.status !== "parsed" && f.status !== "ignored")))
      .catch(() => {});
  }, [migration?.id]);

  if (!beleg.length) return null;

  const reprocess = async () => {
    setBusy(true);
    try {
      await api.post("/migration/analyze", { ai_consent: true });
      toast.success("Belege werden jetzt per KI verarbeitet…");
      onReprocess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verarbeitung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const preview = async (f) => {
    setPreviewing(f.id);
    try {
      const res = await api.get(`/migration/files/${f.id}/content`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau nicht verfügbar");
    } finally { setPreviewing(null); }
  };

  const reasonOf = (f) =>
    f.status === "error" ? (f.error || "KI-Erkennung fehlgeschlagen")
      : "Noch nicht ausgelesen (keine KI-Zustimmung)";

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="unprocessed-beleg-banner">
      <div className="flex items-start gap-3">
        <FileWarning className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-amber-900" data-testid="unprocessed-beleg-count">
            {beleg.length} Beleg-Datei(en) noch nicht als Ausgabe erfasst
          </h3>
          <p className="mt-1 text-sm leading-relaxed text-amber-800">
            Diese PDF-/Bild-Belege wurden noch nicht erfolgreich ausgelesen. Prüfe sie unten per Vorschau und
            starte die KI-Verarbeitung (erneut) — die erkannten Ausgaben erscheinen anschließend im Schritt „Ausgaben".
          </p>

          <div className="mt-3 space-y-1.5" data-testid="unprocessed-beleg-list">
            {beleg.map((f) => (
              <div key={f.id} className="flex items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2" data-testid={`unprocessed-beleg-${f.id}`}>
                <FileText className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium text-slate-800">{f.original_name}</div>
                  <div className="truncate text-xs text-amber-700">{reasonOf(f)}</div>
                </div>
                <button
                  onClick={() => preview(f)}
                  disabled={previewing === f.id}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                  data-testid={`preview-beleg-${f.id}`}
                >
                  {previewing === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Vorschau
                </button>
                <button
                  onClick={() => setManualFile(f)}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                  data-testid={`manual-beleg-${f.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Manuell erfassen
                </button>
              </div>
            ))}
          </div>

          <Button size="sm" onClick={reprocess} disabled={busy}
            className="mt-3 bg-amber-600 hover:bg-amber-700" data-testid="reprocess-beleg-btn">
            {busy ? "Startet…" : "Belege jetzt (erneut) mit KI verarbeiten"}
          </Button>
        </div>
      </div>

      {manualFile && (
        <ManualExpenseDialog
          file={manualFile}
          country={country}
          onClose={() => setManualFile(null)}
          onSaved={(fid) => {
            setManualFile(null);
            setBeleg((prev) => prev.filter((x) => x.id !== fid));
            onReprocess();   // lädt Migration neu → Ausgaben-Zähler aktualisiert (kein neuer KI-Lauf)
          }}
        />
      )}
    </div>
  );
}

// ── Dialog: nicht KI-lesbaren Beleg manuell als Ausgabe erfassen ─────────────
const DEFAULT_VAT_BY_COUNTRY = { DE: "19", AT: "20", CH: "8.1" };

function ManualExpenseDialog({ file, country, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    description: (file.original_name || "").replace(/\.[^.]+$/, ""),
    amount: "", date: today,
    vat_rate: DEFAULT_VAT_BY_COUNTRY[country] ?? "",
    vendor: "", category: "other",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Bitte einen gültigen Brutto-Betrag eingeben."); return; }
    setSaving(true);
    try {
      await api.post(`/migration/files/${file.id}/manual-expense`, {
        description: form.description,
        amount: Number(form.amount),
        date: form.date || null,
        vat_rate: form.vat_rate === "" ? null : Number(form.vat_rate),
        vendor: form.vendor || null,
        category: form.category || "other",
      });
      toast.success("Beleg als Ausgabe erfasst");
      onSaved(file.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erfassen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manual-expense-dialog">
        <DialogHeader>
          <DialogTitle>Beleg manuell als Ausgabe erfassen</DialogTitle>
          <DialogDescription>
            Für <span className="font-medium">{file.original_name}</span> — der Original-Beleg bleibt als Anhang
            an der Ausgabe und durchläuft den normalen Import.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Beschreibung</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="manual-field-description" />
          </div>
          <div>
            <Label className="text-xs">Brutto (€)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="manual-field-amount" />
          </div>
          <div>
            <Label className="text-xs">USt-Satz %</Label>
            <Input type="number" step="0.1" placeholder="z.B. 19" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="manual-field-vat" />
          </div>
          <div>
            <Label className="text-xs">Datum</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="manual-field-date" />
          </div>
          <div>
            <Label className="text-xs">Lieferant</Label>
            <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="manual-field-vendor" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Kategorie</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="manual-field-category" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="manual-expense-save">{saving ? "Speichert…" : "Als Ausgabe erfassen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tabelle ──────────────────────────────────────────────────────────────────
function Ampel({ band, validation }) {
  const msgs = (validation || []).filter((v) => v.level !== "ok");
  const dot = <span className={`inline-block h-3 w-3 rounded-full ${BAND_DOT[band] || "bg-slate-300"}`} />;
  if (!msgs.length) return dot;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild><span className="cursor-help">{dot}</span></TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {msgs.map((m, i) => <div key={i} className="text-xs">{m.level === "error" ? "🔴" : "🟡"} {m.message}</div>)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ItemsTable({ category, items, onEdit, onStatus, selected, onToggleSelect, onToggleSelectAll }) {
  const allSelected = items.length > 0 && selected.size === items.length;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm" data-testid={`items-table-${category}`}>
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="w-8 px-3 py-2">
              <Checkbox checked={allSelected} onCheckedChange={onToggleSelectAll} aria-label="Alle auswählen" data-testid="select-all-checkbox" />
            </th>
            <th className="w-8 px-3 py-2"></th>
            {category === "customer" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">E-Mail</th><th className="text-left px-3 py-2">USt-IdNr.</th><th className="text-left px-3 py-2">Ort</th></>}
            {category === "external_invoice" && <><th className="text-left px-3 py-2">Rechnungsnr.</th><th className="text-left px-3 py-2">Datum</th><th className="text-left px-3 py-2">Kunde</th><th className="text-right px-3 py-2">Brutto</th></>}
            {category === "expense" && <><th className="text-left px-3 py-2">Beleg</th><th className="text-left px-3 py-2">Datum</th><th className="text-left px-3 py-2">Kategorie</th><th className="text-right px-3 py-2">Brutto</th></>}
            {category === "article" && <><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Einheit</th><th className="text-right px-3 py-2">Einzelpreis</th><th className="text-left px-3 py-2">USt-Satz</th></>}
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Aktion</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it) => {
            const p = it.payload;
            const sb = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
            return (
              <tr key={it.id} className="hover:bg-slate-50" data-testid={`item-row-${it.id}`}>
                <td className="px-3 py-2">
                  <Checkbox checked={selected.has(it.id)} onCheckedChange={() => onToggleSelect(it.id)} aria-label="Auswählen" data-testid={`select-${it.id}`} />
                </td>
                <td className="px-3 py-2"><Ampel band={it.confidence_band} validation={it.validation} /></td>
                {category === "customer" && <>
                  <td className="px-3 py-2 font-medium">{p.name || <span className="text-red-500">—</span>}</td>
                  <td className="px-3 py-2 text-slate-500">{p.email || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{p.vat_id || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{p.city || "—"}</td>
                </>}
                {category === "external_invoice" && <>
                  <td className="px-3 py-2 font-mono">{p.external_invoice_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {(p.invoice_date || p.date || "").slice(0, 10) ||
                      <span className="text-red-600 font-medium" data-testid={`nodate-${it.id}`}>Datum fehlt</span>}
                  </td>
                  <td className="px-3 py-2">{p.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-medium">{eur(p.amount)}</td>
                </>}
                {category === "expense" && <>
                  <td className="px-3 py-2">{p.invoice_number || p.description?.slice(0, 24) || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {(p.date || p.invoice_date || "").slice(0, 10) ||
                      <span className="text-red-600 font-medium" data-testid={`nodate-${it.id}`}>Datum fehlt</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{p.category || "—"}</td>
                  <td className="px-3 py-2 text-right font-medium">{eur(p.amount)}</td>
                </>}
                {category === "article" && <>
                  <td className="px-3 py-2 font-medium">{p.name || <span className="text-red-500">—</span>}</td>
                  <td className="px-3 py-2 text-slate-500">{p.unit || "—"}</td>
                  <td className="px-3 py-2 text-right font-medium">{p.unit_price != null ? eur(p.unit_price) : <span className="text-amber-600">—</span>}</td>
                  <td className="px-3 py-2 text-slate-500">{p.vat_rate != null ? `${p.vat_rate}%` : <span className="text-amber-600">fehlt</span>}</td>
                </>}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                    {it.duplicate_of && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700"><Copy className="h-2.5 w-2.5 mr-0.5" />Dup</Badge>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onEdit(it)} className="p-1.5 text-slate-400 hover:text-slate-700" title="Bearbeiten" data-testid={`edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onStatus(it.id, "confirmed")} className="p-1.5 text-slate-400 hover:text-emerald-600" title="Bestätigen" data-testid={`confirm-${it.id}`}><Check className="h-4 w-4" /></button>
                    <button onClick={() => onStatus(it.id, "rejected")} className="p-1.5 text-slate-400 hover:text-red-600" title="Ablehnen" data-testid={`reject-${it.id}`}><X className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Inline-Edit-Dialog ───────────────────────────────────────────────────────
const FIELDS = {
  customer: [["name", "Name"], ["email", "E-Mail"], ["vat_id", "USt-IdNr."], ["city", "Ort"], ["postal_code", "PLZ"]],
  external_invoice: [["external_invoice_number", "Rechnungsnr."], ["invoice_date", "Rechnungsdatum"], ["date", "Zahldatum"], ["customer_name", "Kunde"], ["amount", "Brutto"], ["net_amount", "Netto"], ["vat_amount", "USt"], ["vat_rate", "USt-Satz %"]],
  expense: [["description", "Beschreibung"], ["date", "Datum"], ["category", "Kategorie"], ["amount", "Brutto"], ["net_amount", "Netto"], ["vat_rate", "USt-Satz %"], ["vendor", "Lieferant"]],
  article: [["name", "Name"], ["description", "Beschreibung"], ["unit", "Einheit"], ["unit_price", "Einzelpreis"], ["vat_rate", "USt-Satz %"]],
};

// ── Zahldatum-Bulk-Dialog (Phase 6) ──────────────────────────────────────────
function BulkDateDialog({ open, onClose, onApplied }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.post("/migration/items/bulk-date", { payment_date: date || null });
      toast.success(`Zahldatum für ${res.data.updated} Rechnung(en) gesetzt`);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setzen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-date-dialog">
        <DialogHeader>
          <DialogTitle>Zahldatum für alle Rechnungen setzen</DialogTitle>
          <DialogDescription>
            Setzt das Zahldatum aller Ausgangsrechnungen dieser Migration. Lässt du das Feld leer,
            wird je Beleg das <strong>Rechnungsdatum</strong> als Zahldatum übernommen (relevant für die Ist-Versteuerung).
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs">Zahldatum (optional)</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="bulk-date-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={apply} disabled={busy} data-testid="bulk-date-apply">
            {busy ? "Setzt…" : (date ? "Datum setzen" : "Rechnungsdatum übernehmen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ ...item.payload });
  const [saving, setSaving] = useState(false);
  const fields = FIELDS[item.category] || [];

  const save = async () => {
    setSaving(true);
    try {
      const numeric = ["amount", "net_amount", "vat_amount", "vat_rate", "unit_price"];
      const payload = {};
      for (const [k] of fields) {
        let v = form[k];
        if (numeric.includes(k) && v !== "" && v != null) v = Number(v);
        payload[k] = v === "" ? null : v;
      }
      await api.patch(`/migration/items/${item.id}`, { payload });
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="item-edit-dialog">
        <DialogHeader>
          <DialogTitle>Eintrag bearbeiten</DialogTitle>
          <DialogDescription>Korrigiere die erkannten Werte. Manuelle Änderungen gelten als geprüft (grün).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          {fields.map(([k, lbl]) => {
            const isDate = k === "date" || k === "invoice_date";
            return (
              <div key={k}>
                <Label className="text-xs">{lbl}</Label>
                <Input
                  type={isDate ? "date" : "text"}
                  value={isDate ? String(form[k] ?? "").slice(0, 10) : (form[k] ?? "")}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`edit-field-${k}`}
                />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn">{saving ? "Speichert…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
